import React from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'
import {
  SELL_BUTTON_CLICKED,
  AUTHENTICATION_VIEWED,
} from '~/lib/tracking-events'
import { useRouter } from 'next/router'
import Initialize from 'react-facebook/dist/Initialize'
import AuthDialog from '../AuthDialog'
import {
  color,
  space,
  device,
  fontSize,
  radius,
  fontWeight,
  Button,
} from '@ticketswap/solar'
import { LOGOUT_COMPLETED } from '../../lib/tracking-events'
import { track, resetTrackingIdentity, getLinkProps } from '../../utils'
import { useViewer } from '../ViewerContext'
import { BecomePartnerButton } from '~/components/BlobCover/PartnerCover'
import {
  QuestionmarkRounded,
  ChevronRightAlt,
  Ticket,
  List,
  Alert,
  Avatar,
  CreditCard,
  Chat,
  Logout,
  TicketSwap,
  Gear,
} from '@ticketswap/comets'
import getConfig from 'next/config'
import { useMutation } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import cookies from 'js-cookie'

const { ADMIN_URL } = getConfig().publicRuntimeConfig

const INVALIDATE_ACCESS_TOKEN = gql`
  mutation invalidateAccessToken {
    invalidateAccessToken {
      successfullyLoggedOut
    }
  }
`

const StyledButton = styled.button`
  font-family: inherit;
  font-size: ${fontSize[18]};
  text-align: center;
  width: 100%;
  cursor: pointer;
  border: none;
  padding: 0 ${space[16]};
  height: ${space[56]};
  display: inline-flex;
  min-width: 256px;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  line-height: 1;
  color: ${color.space};
  background-color: ${color.stardust};
  border-radius: ${radius.md};
  white-space: nowrap;

  &:hover,
  &:focus {
    color: ${color.space};
    background-color: ${color.stardust};
  }

  &:active,
  &:focus {
    outline: none;
  }

  @media ${device.tablet} {
    padding: 0 ${space[8]};
    background-color: white;
  }
`

const ActionContainer = styled.div`
  @media ${device.tablet} {
    display: none;
  }
`

const Adornment = styled.span`
  transform: translateX(${space[8]});

  @media ${device.tablet} {
    display: none;
  }
`

const Label = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
`

const Badge = styled.span`
  background-color: ${props =>
    props.bgColor ? color[props.bgColor] : 'white'};
  color: ${props => (props.theme === 'dark' ? 'white' : color.spaceMedium)};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: ${space[32]};
  height: ${space[32]};
  border-radius: ${space[16]};
  margin-right: ${space[16]};

  @media ${device.tablet} {
    background-color: ${props =>
      props.bgColor ? color[props.bgColor] : color.spaceLightest};
    color: ${props => (props.theme === 'dark' ? 'white' : color.spaceMedium)};
  }
`

export const MenuButton = React.forwardRef(({ children, ...props }, ref) => (
  <StyledButton as="a" ref={ref} {...props}>
    {children}
    <Adornment>
      <ChevronRightAlt size={24} className="arrow" />
    </Adornment>
  </StyledButton>
))

const Container = styled.div`
  display: grid;
  grid-gap: ${space[8]};

  @media ${device.tablet} {
    grid-gap: 0;
  }
`

const AccountAlert = styled.div`
  background-color: ${({ isActionable }) =>
    isActionable ? color.stardust : color.stardustLighter};
  border-radius: ${radius.md};
  padding: ${space[16]};
  text-align: left;
  position: relative;

  @media ${device.tablet} {
    padding: 0 ${space[8]};
    background-color: transparent;
    margin-bottom: ${space[16]};
  }
`

const AccountAlertTitle = styled.h3`
  font-size: ${fontSize[18]};
  color: ${color.space};
  font-weight: ${fontWeight.regular};

  @media ${device.tablet} {
    color: ${({ isActionable }) => (isActionable ? color.earth : color.space)};
  }
`

const AccountAlertDescription = styled.p`
  color: ${color.spaceMedium};

  @media ${device.tablet} {
    font-size: ${fontSize[16]};
  }
`

const AccountAlertAction = styled(ChevronRightAlt)`
  color: ${color.space};
  position: absolute;
  top: ${space[16]};
  right: ${space[8]};

  @media ${device.tablet} {
    display: none;
  }
`

const Menu = ({ t, type, ...props }) => {
  const { pathname } = useRouter()
  const { viewer, resetViewer } = useViewer()
  const [invalidateAccessToken] = useMutation(INVALIDATE_ACCESS_TOKEN)

  return (
    <Container {...props}>
      {viewer?.accountAlerts &&
        viewer?.accountAlerts?.map(({ title, description, url }, index) => (
          <AccountAlert
            key={index}
            isActionable={!!url}
            {...(!!url ? { as: 'a', href: url.path } : {})}
          >
            <AccountAlertTitle isActionable={!!url}>{title}</AccountAlertTitle>
            <AccountAlertDescription>{description}</AccountAlertDescription>
            {!!url && <AccountAlertAction size={24} />}
          </AccountAlert>
        ))}

      <ActionContainer>
        {type === 'partner' ? (
          <BecomePartnerButton
            loggedIn={Boolean(viewer)}
            type="light"
            width="full"
            t={t}
          />
        ) : (
          <Link href="/sell">
            <Button
              as="a"
              variant="success"
              width="full"
              onClick={() => {
                track(SELL_BUTTON_CLICKED, {
                  pathname,
                })
              }}
            >
              {t('common:menubar.sellTickets')}
            </Button>
          </Link>
        )}
      </ActionContainer>

      {type === 'partner' && (
        <>
          <MenuButton href="/blog">{t('generic.blog')}</MenuButton>
          <MenuButton href="/content/secure-swap">
            {t('common:menubar.secureSwap')}
          </MenuButton>
        </>
      )}

      {!viewer && (
        <AuthDialog onToggle={on => on && track(AUTHENTICATION_VIEWED)}>
          {({ getToggleProps }) => (
            <>
              <MenuButton {...getToggleProps()}>
                {t('common:menubar.login')}
              </MenuButton>
              <Link {...getLinkProps('HOW_DOES_IT_WORK')}>
                <MenuButton>{t('common:menubar.howItWorks')}</MenuButton>
              </Link>
              <Link {...getLinkProps('HELP')} passHref>
                <MenuButton>{t('common:menubar.help')}</MenuButton>
              </Link>
            </>
          )}
        </AuthDialog>
      )}

      {viewer && (
        <>
          <Link {...getLinkProps('BOUGHT_TICKETS')} passHref>
            <MenuButton>
              <Label>
                <Badge bgColor="earth" theme="dark">
                  <Ticket size={20} />
                </Badge>
                {t('menubar.myTickets')}
              </Label>
            </MenuButton>
          </Link>

          <Link {...getLinkProps('PROFILE_FOR_SALE')} passHref>
            <MenuButton>
              <Label>
                <Badge bgColor="titan" theme="dark">
                  <List size={20} />
                </Badge>
                {t('menubar.myListings')}
              </Label>
            </MenuButton>
          </Link>

          <Link {...getLinkProps('TICKET_ALERTS')} passHref>
            <MenuButton>
              <Label>
                <Badge bgColor="sun" theme="dark">
                  <Alert size={20} />
                </Badge>
                {t('menubar.myWantedListings')}
              </Label>
            </MenuButton>
          </Link>

          <Link {...getLinkProps('ARTISTS_YOU_FOLLOW')} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <Avatar size={20} />
                </Badge>
                {t('menubar.artistsYouFollow')}
              </Label>
            </MenuButton>
          </Link>

          <Link {...getLinkProps('PROFILE')} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <Gear size={20} />
                </Badge>
                {t('menubar.myProfile')}
              </Label>
            </MenuButton>
          </Link>

          <Link {...getLinkProps('PAYOUTS')} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <CreditCard size={20} />
                </Badge>
                {t('menubar.myPayouts')}
              </Label>
            </MenuButton>
          </Link>

          <Link {...getLinkProps('CONVERSATIONS')} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <Chat size={20} />
                </Badge>
                {t('menubar.myConversations')}
              </Label>
            </MenuButton>
          </Link>

          {viewer.isEmployee && (
            <MenuButton href={ADMIN_URL}>
              <Label>
                <Badge theme="light">
                  <TicketSwap size={20} />
                </Badge>
                {t('menubar.admin')}
              </Label>
            </MenuButton>
          )}

          <Link {...getLinkProps('HELP')} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <QuestionmarkRounded size={20} />
                </Badge>
                {t('menubar.help')}
              </Label>
            </MenuButton>
          </Link>

          <Initialize>
            {({ isReady }) => (
              <MenuButton
                onClick={async () => {
                  await invalidateAccessToken()
                  cookies.remove('token')

                  resetViewer()
                  resetTrackingIdentity()
                  if (isReady) window.FB.logout()
                  track(LOGOUT_COMPLETED)
                }}
              >
                <Label>
                  <Badge theme="light">
                    <Logout size={20} />
                  </Badge>
                  {t('menubar.logout')}
                </Label>
              </MenuButton>
            )}
          </Initialize>
        </>
      )}
    </Container>
  )
}

export default Menu
